import { supabase } from './supabase.js';

const API = import.meta.env.VITE_API_URL || '/api';

// Attach auth token to every request
async function getToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token;
}

async function request(path, options = {}) {
  const token = await getToken();
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }

  // Return raw response for binary (audio)
  if (options.raw) return res;
  return res.json();
}

// ── AUTH ──
export const auth = {
  signup:                 (body) => request('/auth/signup',                  { method: 'POST', body: JSON.stringify(body) }),
  signin:                 (body) => request('/auth/signin',                  { method: 'POST', body: JSON.stringify(body) }),
  signout:                ()     => request('/auth/signout',                 { method: 'POST' }),
  forgotPassword:         (body) => request('/auth/forgot-password',         { method: 'POST', body: JSON.stringify(body) }),
  acceptInvite:           (body) => request('/auth/accept-invite',           { method: 'POST', body: JSON.stringify(body) }),
  inviteFamily:           (body) => request('/auth/invite-family',           { method: 'POST', body: JSON.stringify(body) }),
  me:                     ()     => request('/auth/me'),
  updateStorytellerEmail:  (body) => request('/auth/update-storyteller-email',  { method: 'POST', body: JSON.stringify(body) }),
  updateCrewEmail:         (body) => request('/auth/update-crew-email',       { method: 'POST', body: JSON.stringify(body) }),
  claimStorytellerProfile: (body) => request('/auth/claim-storyteller-profile', { method: 'POST', body: JSON.stringify(body) }),
};

// ── SESSIONS ──
export const sessions = {
  list:            (storytellerId) => request(`/sessions/${storytellerId}`),
  start:           (body)          => request('/sessions/start',           { method: 'POST', body: JSON.stringify(body || {}) }),
  complete:        (id, body)      => request(`/sessions/${id}/complete`,  { method: 'POST', body: JSON.stringify(body) }),
  exchange:        (id, body)      => request(`/sessions/${id}/exchange`,  { method: 'POST', body: JSON.stringify(body) }),
  pendingQuestions:(id)            => request(`/sessions/${id}/pending-questions`),
  // HEAD-check that the recorded audio actually landed in R2. Used by the
  // recovery queue before deleting the local IndexedDB copy. Returns
  // { exists, sizeBytes, audioKey }.
  verifyAudio:     (id)            => request(`/sessions/${id}/verify-audio`),
  // Multipart-form audio upload. Used by the live recording flow AND by the
  // background recovery retry. Returns { ok, audioKey } from the backend.
  async uploadAudio(id, audioBlob, segmentIndex) {
    const token = await getToken();
    const fd = new FormData();
    fd.append('audio', audioBlob, 'recording.webm');
    if (segmentIndex !== undefined && segmentIndex !== null) {
      fd.append('segmentIndex', String(segmentIndex));
    }
    const res = await fetch(`${API}/sessions/${id}/audio`, {
      method: 'POST',
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    return res.json();
  },
  async reportAudioFailure(id, body) {
    const token = await getToken();
    const res = await fetch(`${API}/sessions/${id}/audio-failed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${res.status}`);
    }
    return res.json();
  },
};

// ── TTS — returns audio blob ──
export const tts = {
  async speak(text, voicePref = 'male', voiceId = null) {
    const res = await request('/tts', {
      method: 'POST',
      body: JSON.stringify({ text, voicePref, voiceId }),
      raw: true,
    });
    return res.blob();
  },
};

// ── CHAPTERS ──
export const chapters = {
  list:        (storytellerId) => request(`/chapters/${storytellerId}`),
  update:      (id, body)      => request(`/chapters/${id}`,             { method: 'PATCH', body: JSON.stringify(body) }),
  reorder:     (order)         => request('/chapters/reorder',           { method: 'POST',  body: JSON.stringify({ order }) }),
  weavePreface:(storytellerId) => request(`/chapters/${storytellerId}/weave-preface`, { method: 'POST' }),
  create:      (body)          => request('/chapters/create',            { method: 'POST',  body: JSON.stringify(body) }),
  remove:      (id)            => request('/chapters/' + id,             { method: 'DELETE' }),
  combine:     (body)          => request('/chapters/combine',           { method: 'POST',  body: JSON.stringify(body) }),
  generate:      (storytellerId) => request('/chapters/generate',          { method: 'POST',  body: JSON.stringify({ storytellerId }) }),
  extractTopics: (storytellerId) => request('/chapters/extract-topics',    { method: 'POST',  body: JSON.stringify({ storytellerId }) }),
};

// ── ENDNOTES ──
export const endnotes = {
  list:                (chapterId)  => request('/endnotes/chapter/' + chapterId),
  create:              (body)       => request('/endnotes',           { method: 'POST',   body: JSON.stringify(body) }),
  update:              (id, body)   => request('/endnotes/' + id,     { method: 'PATCH',  body: JSON.stringify(body) }),
  remove:              (id)         => request('/endnotes/' + id,     { method: 'DELETE' }),
  removeAllForChapter: (chapterId)  => request('/endnotes/chapter/' + chapterId, { method: 'DELETE' }),
};

// ── ME / MULTI-PROJECT ──
// One-call access to the user's project memberships and the active-project
// pointer used by the project switcher.
export const me = {
  projects:           ()                       => request('/me/projects'),
  setActiveProject:   (storytellerProfileId)   => request('/me/active-project',
                                                          { method: 'POST', body: JSON.stringify({ storytellerProfileId }) }),
  leaveProject:       (storytellerProfileId)   => request('/me/leave-project/' + storytellerProfileId,
                                                          { method: 'POST' }),
  featureFlags:       ()                       => request('/me/feature_flags'),
};

// Public (no-auth) variant — returns only the global flag state so
// anonymous /join visitors can see the template chooser.
export const publicFeatureFlags = {
  fetch: () => request('/feature_flags'),
};

// ── LEGACY & WISHES ──
// Per LEGACY-AND-WISHES-DESIGN.md. Every endpoint here returns
// 403 FEATURE_DISABLED until the matching global flag is on. Callers
// should check feature flags before showing the corresponding UI;
// errors here aren't bugs, they're "feature off for this user."

export const storytellerSettings = {
  updateTemplate: (storytellerId, project_template) =>
    request('/storyteller-settings/' + storytellerId + '/template', {
      method: 'PATCH',
      body: JSON.stringify({ project_template }),
    }),
  setPosthumousDelivery: (storytellerId, enabled) =>
    request('/storyteller-settings/' + storytellerId + '/posthumous-delivery', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
};

export const trustees = {
  list:   (storytellerId)         => request('/trustees/by-storyteller/' + storytellerId),
  create: (body)                  => request('/trustees',           { method: 'POST',   body: JSON.stringify(body) }),
  update: (id, body)              => request('/trustees/' + id,     { method: 'PATCH',  body: JSON.stringify(body) }),
  remove: (id)                    => request('/trustees/' + id,     { method: 'DELETE' }),
};

export const wishes = {
  list:   (storytellerId)         => request('/wishes-sections/by-storyteller/' + storytellerId),
  save:   (storytellerId, sectionKey, content) =>
    request('/wishes-sections/' + storytellerId + '/' + sectionKey, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
};

export const recipients = {
  list:   (storytellerId)         => request('/recipients/by-storyteller/' + storytellerId),
  create: (body)                  => request('/recipients',         { method: 'POST',   body: JSON.stringify(body) }),
  update: (id, body)              => request('/recipients/' + id,   { method: 'PATCH',  body: JSON.stringify(body) }),
  remove: (id)                    => request('/recipients/' + id,   { method: 'DELETE' }),
};

export const letters = {
  list:   (storytellerId)         => request('/letters/by-storyteller/' + storytellerId),
  create: (body)                  => request('/letters',            { method: 'POST',   body: JSON.stringify(body) }),
  update: (id, body)              => request('/letters/' + id,      { method: 'PATCH',  body: JSON.stringify(body) }),
  remove: (id)                    => request('/letters/' + id,      { method: 'DELETE' }),
};

export const posthumous = {
  confirmPassing: (body)          => request('/posthumous/confirm-passing', { method: 'POST', body: JSON.stringify(body) }),
  cancel:         (body)          => request('/posthumous/cancel',          { method: 'POST', body: JSON.stringify(body) }),
  iAmHere:        (body)          => request('/posthumous/i-am-here',       { method: 'POST', body: JSON.stringify(body) }),
  checkin:        (body)          => request('/posthumous/checkin',         { method: 'POST', body: JSON.stringify(body) }),
};

// ── FAMILY ──
export const family = {
  members:          (storytellerId) => request(`/family/${storytellerId}`),
  questions:        (storytellerId) => request(`/family/${storytellerId}/questions`),
  suggestQuestion:  (body)          => request('/family/suggest-question',       { method: 'POST', body: JSON.stringify(body) }),
  updateQuestion:   (questionId, body) => request('/family/question/' + questionId, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteQuestion:   (questionId)      => request('/family/question/' + questionId, { method: 'DELETE' }),
  nudge:            (body)             => request('/family/nudge',                   { method: 'POST', body: JSON.stringify(body) }),
  contributions:    (storytellerId) => request(`/family/${storytellerId}/preface-contributions`),
  addContribution:  (body)          => request('/family/preface-contribution',   { method: 'POST', body: JSON.stringify(body) }),
  checkAnswered:    (storytellerId) => request(`/family/${storytellerId}/check-answered-questions`, { method: 'POST' }),
};

// ── BOOK ──
export const book = {
  settings:     (storytellerId)    => request(`/book/${storytellerId}/settings`),
  updateSettings:(storytellerId, body) => request(`/book/${storytellerId}/settings`, { method: 'PATCH', body: JSON.stringify(body) }),
  photos:       (storytellerId)    => request(`/book/${storytellerId}/photos`),
  updatePhoto:  (photoId, body)    => request(`/book/photos/${photoId}`,  { method: 'PATCH', body: JSON.stringify(body) }),
  deletePhoto:  (photoId)          => request(`/book/photos/${photoId}`,  { method: 'DELETE' }),

  async uploadPhoto(storytellerId, file, chapterId = null) {
    const token = await getToken();
    const formData = new FormData();
    formData.append('photo', file);
    if (chapterId) formData.append('chapterId', chapterId);

    const res = await fetch(`${API}/book/${storytellerId}/upload-photo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) throw new Error('Photo upload failed');
    return res.json();
  },

  async uploadCoverPhotoOriginal(storytellerId, file) {
    const token = await getToken();
    const formData = new FormData();
    formData.append('photo', file);
    const res = await fetch(`${API}/book/${storytellerId}/cover-photo/original`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      let msg = 'Cover photo upload failed';
      try { const j = await res.json(); if (j.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    return res.json();
  },

  async uploadCoverPhotoCropped(storytellerId, blob, crop) {
    const token = await getToken();
    const formData = new FormData();
    formData.append('photo', blob, 'cover.jpg');
    if (crop) formData.append('crop', JSON.stringify(crop));
    const res = await fetch(`${API}/book/${storytellerId}/cover-photo/cropped`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      let msg = 'Cropped cover upload failed';
      try { const j = await res.json(); if (j.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    return res.json();
  },

  // ── BOOK SUGGESTIONS ──
  suggestions:        (storytellerId)         => request(`/book/${storytellerId}/suggestions`),
  createSuggestion:   (storytellerId, body)   => request(`/book/${storytellerId}/suggestions`, { method: 'POST', body: JSON.stringify(body) }),
  updateSuggestion:   (id, body)              => request(`/book/suggestions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSuggestion:   (id)                    => request(`/book/suggestions/${id}`, { method: 'DELETE' }),
};

// ── PHOTO UPSCALING ──
export const upscale = {
  balance:   ()                  => request('/upscale/balance'),
  batch:     (photoIds)          => request('/upscale/batch', { method: 'POST', body: JSON.stringify({ photoIds }) }),
  checkout:  ()                  => request('/upscale/checkout', { method: 'POST' }),
};

// ── ORDERS ──
export const orders = {
  createIntent: (body) => request('/orders/create-intent', { method: 'POST', body: JSON.stringify(body) }),
  get:          (id)   => request(`/orders/${id}`),
};

// ── NOTIFICATIONS ──
export const notifications = {
  list:       ()    => request('/notifications'),
  unreadCount:()    => request('/notifications/unread-count'),
  markRead:   (id)  => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead:()    => request('/notifications/read-all',   { method: 'PATCH' }),
};

// ── CAPTAIN CHAT ──
export const captainChat = {
  list:    (storytellerProfileId) => request('/captain-chat/' + storytellerProfileId),
  send:    (body)                 => request('/captain-chat/send',    { method: 'POST', body: JSON.stringify(body) }),
  intake:  (body)                 => request('/captain-chat/intake',  { method: 'POST', body: JSON.stringify(body) }),
  markRead:(storytellerProfileId) => request('/captain-chat/' + storytellerProfileId + '/mark-read', { method: 'PATCH' }),
};

// ── PREFACE ──
export const preface = {
  get:     (storytellerProfileId) => request('/preface/' + storytellerProfileId),
  approve: (storytellerProfileId, body) => request('/preface/' + storytellerProfileId + '/approve', { method: 'POST', body: JSON.stringify(body) }),
};

// ── ADMIN ──
export const admin = {
  me:                  ()                    => request('/admin/me'),
  systemPrompts:       ()                    => request('/admin/system-prompts'),
  updateSystemPrompt:  (key, body)           => request('/admin/system-prompts/' + encodeURIComponent(key), { method: 'PATCH', body: JSON.stringify(body) }),
  createSystemPrompt:  (body)                => request('/admin/system-prompts', { method: 'POST', body: JSON.stringify(body) }),
  deleteSystemPrompt:  (key)                 => request('/admin/system-prompts/' + encodeURIComponent(key), { method: 'DELETE' }),
  backfillMp3:         ()                    => request('/admin/backfill-mp3', { method: 'POST' }),
  backfillMp3Status:   ()                    => request('/admin/backfill-mp3/status'),
  // Whisper usage backfill — fixes sessions whose Whisper run was skipped
  // by the audioKey-missing-on-/complete bug.
  backfillWhisperPreview: ()                 => request('/admin/backfill-whisper/preview'),
  backfillWhisperStatus:  ()                 => request('/admin/backfill-whisper/status'),
  backfillWhisper:        ()                 => request('/admin/backfill-whisper', { method: 'POST' }),
  // Audio integrity audit — finds sessions whose audio is missing in R2.
  auditAudio:             (daysBack = 7)     => request('/admin/audit-audio?daysBack=' + daysBack),
  activeUsers:            (minutes = 5)      => request('/admin/active-users?minutes=' + minutes),
  dictionarySignals:      (days = 90, minCount = 1) =>
                            request('/admin/dictionary-signals?days=' + days + '&minCount=' + minCount),
  // Usage reporting
  usage:               (period = 'daily')    => request('/admin/usage?period=' + encodeURIComponent(period)),
  usageRange:          (start, end)          => request('/admin/usage/range?start=' + encodeURIComponent(start) + '&end=' + encodeURIComponent(end)),
  storytellerUsage:    (storytellerId)       => request('/admin/usage/storyteller/' + encodeURIComponent(storytellerId)),
  storytellerCalls:    (storytellerId, period = 'today', start = null, end = null) => {
    let qs = 'period=' + encodeURIComponent(period);
    if (period === 'range' && start && end) {
      qs += '&start=' + encodeURIComponent(start) + '&end=' + encodeURIComponent(end);
    }
    return request('/admin/usage/storyteller/' + encodeURIComponent(storytellerId) + '/calls?' + qs);
  },
  usageCalls:          (start, end) =>
    request('/admin/usage/calls?start=' + encodeURIComponent(start) + '&end=' + encodeURIComponent(end)),
  // Infrastructure costs (fixed monthly subscriptions)
  infrastructureCosts: ()                    => request('/admin/infrastructure-costs'),
  createInfraCost:     (body)                => request('/admin/infrastructure-costs', { method: 'POST', body: JSON.stringify(body) }),
  updateInfraCost:     (id, body)            => request('/admin/infrastructure-costs/' + id, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteInfraCost:     (id)                  => request('/admin/infrastructure-costs/' + id, { method: 'DELETE' }),
  // Generic admin settings (daily email toggle, recipient, etc.)
  settings:            ()                    => request('/admin/settings'),
  updateSetting:       (key, value)          => request('/admin/settings/' + encodeURIComponent(key), { method: 'PATCH', body: JSON.stringify({ value }) }),
  // Per-unit service rates (editable pricing)
  serviceRates:        ()                    => request('/admin/service-rates'),
  updateServiceRate:   (id, body)            => request('/admin/service-rates/' + id, { method: 'PATCH', body: JSON.stringify(body) }),
  // Monthly quotas (used vs. allowance for subscriptions like ElevenLabs)
  quotas:              ()                    => request('/admin/quotas'),

  // Legacy & wishes admin (Phase 14)
  legacyFlags:         ()                    => request('/admin/legacy/flags'),
  setLegacyFlag:       (key, enabled)        => request('/admin/legacy/flags/' + key, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
  legacyCohort:        (flag_key)            => request('/admin/legacy/cohort' + (flag_key ? '?flag_key=' + flag_key : '')),
  addToCohort:         (body)                => request('/admin/legacy/cohort', { method: 'POST', body: JSON.stringify(body) }),
  removeFromCohort:    (id)                  => request('/admin/legacy/cohort/' + id, { method: 'DELETE' }),
  legacyAudit:         (limit = 100, event)  => request('/admin/legacy/audit?limit=' + limit + (event ? '&event=' + event : '')),
  legacyDeliveriesInWindow: ()               => request('/admin/legacy/deliveries-in-window'),
};

// ── BETA REPORTS ──
export const reports = {
  sendIssue: (body) => request('/reports/issue', { method: 'POST', body: JSON.stringify(body) }),
};

// ── BILLING ──
export const billing = {
  storytellerSlots:        ()             => request('/billing/storyteller-slots'),
  purchaseStorytellerSlot: (tier, parentStorytellerId) => request('/billing/purchase-storyteller-slot', { method: 'POST', body: JSON.stringify({ tier: tier || 'story', parentStorytellerId: parentStorytellerId || null }) }),
};

// ── NAME DICTIONARY ──
export const dictionary = {
  list:    (storytellerId)     => request('/dictionary/' + storytellerId),
  create:  (storytellerId, body) => request('/dictionary/' + storytellerId, { method: 'POST', body: JSON.stringify(body) }),
  update:  (id, body)          => request('/dictionary/' + id,          { method: 'PATCH', body: JSON.stringify(body) }),
  remove:  (id)                => request('/dictionary/' + id,          { method: 'DELETE' }),
  ignore:  (storytellerId, word) => request('/dictionary/' + storytellerId + '/ignore', { method: 'POST', body: JSON.stringify({ word }) }),
  // Captain says "this is a regular word, not a name." Backend lowercases
  // every occurrence across all transcripts AND adds to the ignored list.
  lowercase: (storytellerId, word) => request('/dictionary/' + storytellerId + '/lowercase', { method: 'POST', body: JSON.stringify({ word }) }),
  // Pre-seeds the dictionary with proper nouns from the storyteller's bio
  // so Whisper has them BEFORE the first interview. Idempotent — safe to
  // call after every bio save.
  extractFromBio: (storytellerId) =>
    request('/dictionary/' + storytellerId + '/extract-from-bio', { method: 'POST' }),
};

// ── UPLOADS ──
export const uploads = {
  presign:              (body)          => request('/uploads/presign', { method: 'POST', body: JSON.stringify(body) }),
  transcripts:          (storytellerId) => request('/uploads/transcripts/' + storytellerId),
  updateTranscript:     (source, id, body) => request('/uploads/transcripts/' + source + '/' + id, { method: 'PATCH', body: JSON.stringify(body) }),
  regenerateChapters:   (storytellerId) => request('/uploads/regenerate-chapters', { method: 'POST', body: JSON.stringify({ storytellerId }) }),
  regenerationStatus:   (storytellerId) => request('/uploads/regeneration-status/' + storytellerId),
};
